import { HISTORY_KIND_WORDS, HISTORY_KINDS, type AssetDto, type HistoryEntryDto, type HistoryKind, type TimelineDto, type TimelineItemDto } from '@nsb/core';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, errorText, queryClient, useApi } from '../../api.ts';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { Button, ErrorLine, Field, LoadError, Loading, Page, TextInput, Title, Toggle } from '../../components/ui.tsx';
import { useUnsaved } from '../../components/useUnsaved.ts';
import { changedLabels } from '../../unsaved.ts';
import { ProjectCrumb } from './common.tsx';
import { assetsKey, useProject, useProjectId } from './hooks.ts';
import { dateWords, easternToday } from '../../days.ts';

/**
 * The timeline (feature spec "client page, files" §5; Jason, 2026-10-03: "an
 * infinitely scrollable back in time page that shows all the steps, all the
 * things that have happened so far and the communication"). Newest first,
 * the earlier pages loading as it scrolls; each line opens to its detail.
 * NSB writes down what happened before the portal, or outside it, on its day.
 */

const timelineKey = (projectId: string) => ['project', projectId, 'timeline'];

/** One word for each kind of line, before the line (guide §20.2). */
const WHAT: Record<TimelineItemDto['what'], string> = {
  history: 'History',
  files: 'Files',
  review: 'Review',
  conversation: 'Comments',
  mail: 'Email',
  process: 'Process',
  money: 'Money',
  project: 'Project',
  people: 'People',
  time: 'Time',
};

const month = (day: string) => dateWords(day, 'month');
const short = (day: string) => dateWords(day);
const today = easternToday;

export function Timeline() {
  const projectId = useProjectId();
  const { data: project } = useProject(projectId);
  const q = useInfiniteQuery({
    queryKey: timelineKey(projectId),
    queryFn: ({ pageParam }) => api<TimelineDto>(`/projects/${projectId}/timeline${pageParam ? `?before=${encodeURIComponent(pageParam)}` : ''}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
  });
  const [writing, setWriting] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  useFeedbackRecord(projectId, project ? { type: 'project', id: projectId, label: `${project.name} — timeline` } : null);

  // Further back as the end comes into view.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = q;
  useEffect(() => {
    const el = end.current;
    if (!el || !hasNextPage) return;
    const seen = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
      },
      { rootMargin: '600px 0px' },
    );
    seen.observe(el);
    return () => seen.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (q.error) return <LoadError error={q.error} />;
  if (!q.data || !project) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  const staff = project.viewerRole === 'contractor';
  const items = q.data.pages.flatMap((p) => p.items);
  const months: [string, TimelineItemDto[]][] = [];
  for (const item of items) {
    const m = month(item.day);
    if (months.at(-1)?.[0] !== m) months.push([m, []]);
    months.at(-1)![1].push(item);
  }

  return (
    <Page wide>
      <ProjectCrumb project={project} />
      <Title>Timeline</Title>
      <p className="max-w-2xl text-ink-2">
        {staff
          ? 'Everything that has happened on this project, newest first: all that was done in the portal, and the history written down from before it or outside it. Open a line for its detail.'
          : 'Everything that has happened on your project, newest first. Open a line for its detail.'}
      </p>
      {staff ? (
        writing ? (
          <HistoryForm projectId={projectId} onDone={() => setWriting(false)} />
        ) : (
          <div className="mt-8">
            <Button onClick={() => setWriting(true)}>Write down something that happened</Button>
            <p className="meta mt-2">A meeting, a call, a proposal sent, a payment: on the day it happened, yours alone unless you share it.</p>
          </div>
        )
      ) : null}

      {items.length === 0 ? <p className="mt-10 text-ink-3">Nothing yet.</p> : null}
      {months.map(([m, rows]) => (
        <section key={m} className="mt-10">
          <h2 className="label border-b border-ink/40 pb-2">{m}</h2>
          <ol className="divide-y divide-rule">
            {rows.map((item, i) => (
              <Line key={item.id} projectId={projectId} item={item} firstOfDay={i === 0 || rows[i - 1]!.day !== item.day} staff={staff} />
            ))}
          </ol>
        </section>
      ))}
      <div ref={end} className="mt-8">
        {q.hasNextPage ? (
          <Button tone="quiet" busy={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            Further back
          </Button>
        ) : items.length ? (
          <p className="meta">The beginning of the project.</p>
        ) : null}
      </div>
    </Page>
  );
}

function Line({ projectId, item, firstOfDay, staff }: { projectId: string; item: TimelineItemDto; firstOfDay: boolean; staff: boolean }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const h = item.history;
  // A history entry's first line of detail is its kind, already the line's word.
  const detail = h ? item.detail.slice(1) : item.detail;
  const opens = detail.length > 0 || item.link || h;
  return (
    <li className="grid grid-cols-[5.25rem_1fr] gap-x-3 py-3 sm:grid-cols-[6.5rem_1fr] sm:gap-x-4">
      <div className="pt-0.5">
        {firstOfDay ? <span className="num block text-sm text-ink-2">{short(item.day)}</span> : null}
        {item.time ? <span className="num block text-[0.75rem] text-ink-3">{item.time}</span> : null}
        {/* On the day it happened, set after the fact, with the day it was recorded beside it (L-22 A). */}
        {item.recordedOn ? (
          <span className="num block text-[0.75rem] leading-snug text-ink-3">
            recorded <span className="whitespace-nowrap">{dateWords(item.recordedOn, 'date')}</span>
          </span>
        ) : null}
      </div>
      <div className="min-w-0">
        {opens ? (
          <button type="button" className="group block min-h-11 w-full text-left" aria-expanded={open} onClick={() => setOpen(!open)}>
            <span className="label mr-2 align-[0.1em]">{h ? HISTORY_KIND_WORDS[h.kind] : WHAT[item.what]}</span>
            <span className="group-hover:underline">{item.title}</span>
            {staff && h && !h.shared ? <span className="label ml-2 whitespace-nowrap align-[0.1em] !text-cedar">NSB only</span> : null}
          </button>
        ) : (
          <p className="min-h-11">
            <span className="label mr-2 align-[0.1em]">{WHAT[item.what]}</span>
            {item.title}
          </p>
        )}
        {open ? (
          <div className="mb-2 max-w-2xl space-y-1.5 text-sm text-ink-2">
            {detail.map((d, i) => (
              <p key={i} className="whitespace-pre-line">
                {d}
              </p>
            ))}
            <p className="flex flex-wrap gap-x-5">
              {item.link ? (
                <Link to={item.link} className="link">
                  Open it
                </Link>
              ) : null}
              {h?.sourceUrl ? (
                <a href={h.sourceUrl} target="_blank" rel="noreferrer" className="link">
                  On record
                </a>
              ) : null}
              {staff && h && !editing ? (
                <Button tone="quiet" className="hit !min-h-0" onClick={() => setEditing(true)}>
                  Change it
                </Button>
              ) : null}
            </p>
            {editing && h ? <HistoryForm projectId={projectId} entry={h} onDone={() => setEditing(false)} /> : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}

/** A history entry written down, or changed: on its day, with where it is on record. */
function HistoryForm({ projectId, entry, onDone }: { projectId: string; entry?: HistoryEntryDto; onDone: () => void }) {
  const { data: assets } = useApi<AssetDto[]>(assetsKey(projectId), `/projects/${projectId}/assets`);
  const [happenedOn, setOn] = useState(entry?.happenedOn ?? today());
  const [happenedAt, setAt] = useState(entry?.happenedAt ?? '');
  const [kind, setKind] = useState<HistoryKind>(entry?.kind ?? 'meeting');
  const [title, setTitle] = useState(entry?.title ?? '');
  const [body, setBody] = useState(entry?.body ?? '');
  const [sourceUrl, setSource] = useState(entry?.sourceUrl ?? '');
  // The file it is about: its first; any others put with it on a step's log stay with it (design study 31, slice 3).
  const [assetId, setAsset] = useState(entry?.files[0]?.id ?? '');
  const others = (entry?.files ?? []).slice(1).map((f) => f.id);
  const [shared, setShared] = useState(entry?.shared ?? false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);

  const done = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await queryClient.invalidateQueries({ queryKey: timelineKey(projectId) });
      onDone();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const save = () => {
    const b = { happenedOn, happenedAt: happenedAt || null, kind, title: title.trim(), body: body.trim() || null, sourceUrl: sourceUrl.trim() || null, fileIds: [...(assetId ? [assetId] : []), ...others.filter((o) => o !== assetId)], shared };
    return done(() => (entry ? api(`/projects/${projectId}/history/${entry.id}`, { method: 'PATCH', body: b }) : api(`/projects/${projectId}/history`, { body: b })));
  };
  // An entry being changed (its Save), changed and not saved, on the bar at the window's foot (P-29). One being written
  // down for the first time is a new thing made, as Write it down says, not a change to what is there.
  const now = { happenedOn, happenedAt, kind, title, body, sourceUrl, assetId, shared };
  useUnsaved({
    names: entry
      ? changedLabels(
          { happenedOn: entry.happenedOn, happenedAt: entry.happenedAt, kind: entry.kind, title: entry.title, body: entry.body, sourceUrl: entry.sourceUrl, assetId: entry.files[0]?.id ?? null, shared: entry.shared },
          now,
          [
            ['The day', (x) => x.happenedOn],
            ['The time', (x) => x.happenedAt],
            ['What it was', (x) => x.kind],
            ['What happened', (x) => x.title],
            ['The detail', (x) => x.body],
            ['Where it is on record', (x) => x.sourceUrl],
            ['The file it is about', (x) => x.assetId],
            ['Share it with the client', (x) => x.shared],
          ],
        )
      : [],
    busy,
    canSave: Boolean(title.trim() && happenedOn),
    error: err,
    onSave: save,
    onDiscard: () => {
      if (!entry) return;
      setOn(entry.happenedOn);
      setAt(entry.happenedAt ?? '');
      setKind(entry.kind);
      setTitle(entry.title);
      setBody(entry.body ?? '');
      setSource(entry.sourceUrl ?? '');
      setAsset(entry.files[0]?.id ?? '');
      setShared(entry.shared);
      setErr(null);
    },
  });

  return (
    <div className="mt-6 max-w-2xl border border-rule bg-sheet p-4 sm:p-5">
      <p className="label mb-4">{entry ? 'Change it' : 'Something that happened'}</p>
      <div className="grid gap-x-6 sm:grid-cols-2">
        <Field label="The day">{(id) => <TextInput id={id} type="date" max={today()} value={happenedOn} onChange={(e) => setOn(e.target.value)} />}</Field>
        <Field label="The time" hint="Optional.">
          {(id) => <TextInput id={id} type="time" value={happenedAt} onChange={(e) => setAt(e.target.value)} />}
        </Field>
      </div>
      <div className="mb-5" role="group" aria-label="What it was">
        <p className="label mb-2">What it was</p>
        <div className="flex flex-wrap gap-2">
          {HISTORY_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={kind === k}
              onClick={() => setKind(k)}
              className={`min-h-10 rounded-[2px] border px-3 text-[0.8125rem] transition-colors duration-[120ms] ${kind === k ? 'border-ink bg-ink text-paper' : 'border-rule-strong text-ink-2 hover:border-ink hover:text-ink'}`}
            >
              {HISTORY_KIND_WORDS[k]}
            </button>
          ))}
        </div>
      </div>
      <Field label="What happened" hint="One line, as it would read on the timeline.">
        {(id) => <TextInput id={id} value={title} onChange={(e) => setTitle(e.target.value)} />}
      </Field>
      <Field label="The detail" hint="Optional: who was there, what was discussed, what was decided.">
        {(id) => <textarea id={id} rows={3} className="field resize-y" value={body} onChange={(e) => setBody(e.target.value)} />}
      </Field>
      <Field label="Where it is on record" hint="Optional: the Gmail thread, the Drive file, the Xero document. NSB’s only.">
        {(id) => <TextInput id={id} type="url" inputMode="url" value={sourceUrl} onChange={(e) => setSource(e.target.value)} placeholder="https://…" />}
      </Field>
      <Field label="The file it is about" hint="Optional, from the project’s files.">
        {(id) => (
          <select id={id} className="field" value={assetId} onChange={(e) => setAsset(e.target.value)}>
            <option value="">None</option>
            {(assets ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Toggle checked={shared} onChange={setShared} label="Share it with the client" hint="It shows on their timeline; where it is on record never does." />
      <div className="mt-3 flex flex-wrap gap-x-5">
        <Button busy={busy} disabled={!title.trim() || !happenedOn} onClick={() => void save()}>
          {entry ? 'Save' : 'Write it down'}
        </Button>
        <Button tone="quiet" onClick={onDone}>
          Cancel
        </Button>
        {entry && !withdrawing ? (
          <Button tone="quiet" onClick={() => setWithdrawing(true)}>
            Withdraw it
          </Button>
        ) : null}
      </div>
      {entry && withdrawing ? (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 text-sm">
          <span>Take it off the timeline? It is kept, with who withdrew it.</span>
          <Button tone="danger" busy={busy} onClick={() => void done(() => api(`/projects/${projectId}/history/${entry.id}/withdraw`, { body: {} }))}>
            Yes
          </Button>
          <Button tone="quiet" onClick={() => setWithdrawing(false)}>
            No
          </Button>
        </div>
      ) : null}
      <ErrorLine error={err} />
    </div>
  );
}
